// Adapted from node_modules/@salesforce/retail-react-app/app/components/drawer-menu/drawer-menu.jsx
import React from 'react'
import PropTypes from 'prop-types'
import {useCategory} from '@salesforce/commerce-sdk-react'
import {
    CAT_MENU_DEFAULT_NAV_SSR_DEPTH,
    CAT_MENU_DEFAULT_ROOT_CATEGORY
} from '@salesforce/retail-react-app/app/constants'
import {flatten} from '@salesforce/retail-react-app/app/utils/utils'
import {withCommerceSdkReact} from '@salesforce/retail-react-app/app/components/with-commerce-sdk-react/with-commerce-sdk-react'
import NestedAccordion from '~/app/components/nested-accordion'
import Link from '~/app/components/link'
import {categoryUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'

const PlaceholderComponent = () => (
    <div className="flex items-center justify-center p-2" data-testid="categories-Center">
        <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-current border-t-transparent"
            data-testid="categories-Spinner"
        />
    </div>
)

const DrawerMenuItemWithData = withCommerceSdkReact(
    ({itemComponent: ItemComponent, data, ...rest}) => (
        <div className="animate-fadeIn" data-testid="categories-Fade">
            <ItemComponent
                {...rest}
                item={data}
                itemComponent={DrawerMenuItemWithData}
                data-testid="categories-ItemComponent"
            />
        </div>
    ),
    {
        hook: useCategory,
        queryOptions: ({item}) => ({
            parameters: {
                id: item.id
            }
        }),
        placeholder: PlaceholderComponent
    }
)

const FONT_SIZE_MAP = {lg: 'text-lg', md: 'text-base'}
const FONT_WEIGHT_MAP = {semibold: 'font-semibold', regular: 'font-normal'}

export function Categories() {
    const {data: categoriesTree} = useCategory({
        parameters: {id: CAT_MENU_DEFAULT_ROOT_CATEGORY, levels: CAT_MENU_DEFAULT_NAV_SSR_DEPTH}
    })
    const categories = flatten(categoriesTree || {}, 'categories')
    const root = categories?.[CAT_MENU_DEFAULT_ROOT_CATEGORY]
    const itemsKey = 'categories'
    const itemsCountKey = 'onlineSubCategoriesCount'

    const FONT_SIZES = ['lg', 'md', 'md']
    const FONT_WEIGHTS = ['semibold', 'semibold', 'regular']
    return (
        <div id="category-nav" aria-live="polite" aria-atomic="true">
            {/* Category Navigation */}
            {root?.[itemsKey] ? (
                <div className="animate-fadeIn" data-testid="categories-Fade">
                    <NestedAccordion
                        allowMultiple={true}
                        item={root}
                        itemsCountKey={itemsCountKey}
                        itemsKey={itemsKey}
                        itemsFilter="c_showInMenu"
                        fontSizes={FONT_SIZES}
                        fontWeights={FONT_WEIGHTS}
                        itemsBefore={({depth, item}) =>
                            depth > 0 ? (
                                [
                                    <div key="show-all" className="border-none">
                                        <Link
                                            to={categoryUrlBuilder(item)}
                                            className={`block pl-8 py-2 text-black no-underline hover:underline ${
                                                FONT_SIZE_MAP[FONT_SIZES[depth]] || 'text-base'
                                            } ${
                                                FONT_WEIGHT_MAP[FONT_WEIGHTS[depth]] ||
                                                'font-normal'
                                            }`}
                                            data-testid="categories-AccordionButton"
                                        >
                                            Shop All
                                        </Link>
                                    </div>
                                ]
                            ) : (
                                <></>
                            )
                        }
                        urlBuilder={categoryUrlBuilder}
                        itemComponent={DrawerMenuItemWithData}
                        data-testid="categories-NestedAccordion"
                    />
                </div>
            ) : (
                <div
                    className="flex items-center justify-center p-8"
                    data-testid="categories-Center"
                >
                    <div
                        className="h-10 w-10 animate-spin rounded-full border-4 border-current border-t-transparent"
                        data-testid="categories-Spinner"
                    />
                </div>
            )}
        </div>
    )
}

Categories.propTypes = {
    children: PropTypes.node
}

export const CategoriesDefinition = {
    component: Categories,
    name: 'Categories',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/menu.svg',
    defaultChildren: [
        {
            '@type': '@builder.io/sdk:Element',
            canHaveChildren: true,
            component: {name: 'Text', options: {text: 'Shop'}}
        }
    ]
}
